/* lexar-drawer.js — slide-out cart drawer + reserve timer (fleet-shared).
   Core rules match reconciler v2: totals only, never consolidate split
   lines, paid first, then raise gifts[0], then trim the last gift line.
   The Checkout button carries name="checkout" so the theme's existing
   document-delegated intercept builds the portal payload (gifts 0.00,
   paid at original price). */
(function (root) {
  'use strict';

  /* ============================ core ============================ */

  function freeForPaid(p) { return Math.floor(p / 2); }

  function tally(cart, vid) {
    var paid = 0, paidLines = [], gifts = [];
    (cart.items || []).forEach(function (li) {
      if (li.variant_id !== vid) return;
      if (li.properties && li.properties._free_gift === 'true') gifts.push(li);
      else { paid += li.quantity; paidLines.push(li); }
    });
    var giftQty = gifts.reduce(function (s, g) { return s + g.quantity; }, 0);
    return { paid: paid, paidLines: paidLines, gifts: gifts, giftQty: giftQty };
  }

  /* one pass -> a single mutation descriptor, or null when settled */
  function reconcileOp(cart, vid, intendedPaid) {
    var t = tally(cart, vid);
    var basis = intendedPaid != null ? intendedPaid : t.paid;
    var target = freeForPaid(basis);
    if (intendedPaid != null && t.paid < intendedPaid) {
      if (t.paidLines.length) {
        var first = t.paidLines[0];
        return { type: 'change', key: first.key, quantity: first.quantity + (intendedPaid - t.paid) };
      }
      return { type: 'add', id: vid, quantity: intendedPaid - t.paid };
    }
    if (intendedPaid != null && t.paid > intendedPaid) {
      var last = t.paidLines[t.paidLines.length - 1];
      var cut = Math.min(t.paid - intendedPaid, last.quantity);
      return { type: 'change', key: last.key, quantity: last.quantity - cut };
    }
    if (t.giftQty < target) {
      if (t.gifts.length) {
        return { type: 'change', key: t.gifts[0].key, quantity: t.gifts[0].quantity + (target - t.giftQty) };
      }
      return { type: 'add', id: vid, quantity: target, properties: { _free_gift: 'true' } };
    }
    if (t.giftQty > target) {
      var lg = t.gifts[t.gifts.length - 1];
      var c = Math.min(t.giftQty - target, lg.quantity);
      return { type: 'change', key: lg.key, quantity: lg.quantity - c };
    }
    return null;
  }

  /* drive to fixpoint: <=8 passes, one mutation per pass, fresh read each */
  function settle(io, vid, intendedPaid) {
    var passes = 0;
    function step() {
      if (passes >= 8) return Promise.resolve(passes);
      return io.getCart().then(function (cart) {
        var op = reconcileOp(cart, vid, intendedPaid);
        if (!op) return passes;
        passes++;
        var run = op.type === 'add' ? io.add(op) : io.change(op);
        return run.then(step);
      });
    }
    return step();
  }

  /* ============================ timer ============================ */

  function fmt(ms) {
    if (ms < 0) ms = 0;
    var s = Math.floor(ms / 1000), m = Math.floor(s / 60);
    s = s % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }
  function tick(now, until) {
    if (until == null) return { remain: 0, expired: false };
    var remain = Math.max(0, until - now);
    return { remain: remain, expired: now >= until };
  }

  var core = { freeForPaid: freeForPaid, tally: tally, reconcileOp: reconcileOp, settle: settle, fmt: fmt, tick: tick };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof root !== 'undefined') root.LXD = core;
  if (typeof document === 'undefined') return;

  /* ============================ DOM ============================ */

  var drawer, busy = false, dirty = true, mutatedOnCartPage = false;
  var KEY = 'lxdReserveUntil', armOnAdd = false, timerId = null;

  function $(sel, el) { return (el || drawer).querySelector(sel); }
  function cartPath() { return (drawer && drawer.getAttribute('data-cart-url')) || '/cart'; }
  function onCartPage() { return location.pathname.replace(/\/$/, '') === cartPath().replace(/\/$/, ''); }

  var io = {
    getCart: function () { return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }); },
    change: function (op) {
      return fetch('/cart/change.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: op.key, quantity: op.quantity })
      }).then(function (r) { return r.json(); });
    },
    add: function (op) {
      var item = { id: op.id, quantity: op.quantity };
      if (op.properties) item.properties = op.properties;
      return fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      }).then(function (r) { return r.json(); });
    }
  };

  /* ---- open / close ---- */
  function openDrawer() {
    if (!drawer) return;
    if (dirty) refresh();
    drawer.classList.add('lxd--open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lxd-lock');
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('lxd--open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lxd-lock');
    if (mutatedOnCartPage && onCartPage()) { mutatedOnCartPage = false; location.reload(); }
  }

  /* ---- refresh via section rendering ---- */
  var refreshT = null;
  function scheduleRefresh() {
    dirty = true;
    if (!drawer || !drawer.classList.contains('lxd--open')) return;
    clearTimeout(refreshT);
    refreshT = setTimeout(refresh, 350);
  }
  function refresh() {
    if (!drawer) return;
    dirty = false;
    fetch(location.pathname + '?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector('[data-lxd]');
        if (!next) return;
        var items = $('[data-lxd-items]'), nItems = next.querySelector('[data-lxd-items]');
        if (items && nItems) items.innerHTML = nItems.innerHTML;
        ['[data-lxd-count]', '[data-lxd-saved]', '[data-lxd-subtotal]'].forEach(function (sel) {
          var a = $(sel), b = next.querySelector(sel);
          if (a && b) a.textContent = b.textContent;
        });
        var savedRow = $('[data-lxd-saved-row]'), nSavedRow = next.querySelector('[data-lxd-saved-row]');
        if (savedRow && nSavedRow) savedRow.hidden = nSavedRow.hidden;
        drawer.setAttribute('data-lxd-total', next.getAttribute('data-lxd-total') || '0');
        var foot = $('[data-lxd-foot]');
        if (foot) foot.hidden = (drawer.getAttribute('data-lxd-total') === '0');
        badges(parseInt(drawer.getAttribute('data-lxd-total'), 10) || 0);
        syncTimer();
      })
      .catch(function () {});
  }

  /* ---- timer ---- */
  function timerMode() { return drawer.getAttribute('data-timer-mode') || 'restart'; }
  function timerMs() { return (parseInt(drawer.getAttribute('data-timer-minutes'), 10) || 15) * 60000; }
  function readUntil() { try { var v = localStorage.getItem(KEY); return v ? parseInt(v, 10) : null; } catch (e) { return null; } }
  function writeUntil(v) { try { if (v == null) localStorage.removeItem(KEY); else localStorage.setItem(KEY, String(v)); } catch (e) {} }

  function syncTimer() {
    var count = parseInt(drawer.getAttribute('data-lxd-total'), 10) || 0;
    var bar = $('[data-lxd-reserve]');
    if (!bar) return;
    if (count <= 0) { writeUntil(null); armOnAdd = false; bar.hidden = true; return; }
    var until = readUntil();
    if (until == null && !armOnAdd) { until = Date.now() + timerMs(); writeUntil(until); }
    bar.hidden = (until == null);
    renderClock();
  }
  function renderClock() {
    var bar = $('[data-lxd-reserve]');
    if (!bar || bar.hidden) return;
    var st = tick(Date.now(), readUntil());
    var clock = $('[data-lxd-clock]');
    if (clock) clock.textContent = fmt(st.remain);
    if (st.expired) onExpire(bar);
  }
  function onExpire(bar) {
    var mode = timerMode();
    writeUntil(null);
    if (mode === 'clear') {
      bar.hidden = true;
      fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' }).then(function () { scheduleRefresh(); refresh(); });
    } else {
      bar.hidden = true;
      if (mode === 'restart') armOnAdd = true;
    }
  }
  function armTimerFromAdd() {
    var until = readUntil();
    if (until == null) { writeUntil(Date.now() + timerMs()); armOnAdd = false; }
  }

  /* ---- header badges (the cart page used to own these) ---- */
  function badges(count) {
    document.querySelectorAll('#cartCount,#cartBadge,[data-cart-count]').forEach(function (b) {
      b.textContent = count;
      if (b.classList) b.classList.toggle('zero', count === 0);
    });
  }

  /* ---- page-load heal: carts built before this theme (or edited elsewhere)
         may hold paid units with no marked free line — reconcile every
         variant once, like the old cart page's sweep did ---- */
  function sweepAll() {
    return io.getCart().then(function (cart) {
      var seen = {}, vids = [];
      (cart.items || []).forEach(function (li) {
        if (!seen[li.variant_id]) { seen[li.variant_id] = 1; vids.push(li.variant_id); }
      });
      return vids.reduce(function (p, v) {
        return p.then(function (any) {
          return settle(io, v, null).then(function (n) { return any + (n || 0); });
        });
      }, Promise.resolve(0));
    }).then(function (muts) {
      if (muts > 0) {
        dirty = true;
        if (drawer.classList.contains('lxd--open')) refresh();
        io.getCart().then(function (c) { badges(c.item_count || 0); });
      }
    }).catch(function () {});
  }

  /* ---- steppers / remove ---- */
  function op(vid, delta, removeAll) {
    if (busy) return;
    busy = true;
    drawer.classList.add('lxd--busy');
    io.getCart().then(function (cart) {
      var t = tally(cart, vid);
      var intended = removeAll ? 0 : Math.max(0, t.paid + delta);
      return settle(io, vid, intended);
    }).then(function () {
      if (onCartPage()) mutatedOnCartPage = true;
      busy = false;
      drawer.classList.remove('lxd--busy');
      refresh();
    }).catch(function () {
      busy = false;
      drawer.classList.remove('lxd--busy');
      refresh();
    });
  }

  /* ---- wire ---- */
  function init() {
    drawer = document.querySelector('[data-lxd]');
    if (!drawer) return;

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-lxd-allow]')) return;
      var closer = t.closest('[data-lxd-close]');
      if (closer && drawer.contains(closer)) { e.preventDefault(); closeDrawer(); return; }
      var plus = t.closest('[data-lxd-plus]');
      if (plus && drawer.contains(plus)) { e.preventDefault(); op(Number(plus.getAttribute('data-vid')), 1, false); return; }
      var minus = t.closest('[data-lxd-minus]');
      if (minus && drawer.contains(minus)) { e.preventDefault(); op(Number(minus.getAttribute('data-vid')), -1, false); return; }
      var rm = t.closest('[data-lxd-remove]');
      if (rm && drawer.contains(rm)) { e.preventDefault(); op(Number(rm.getAttribute('data-vid')), 0, true); return; }
      var link = t.closest('a[href]');
      if (link && !drawer.contains(link)) {
        var href = link.getAttribute('href') || '';
        var path = href.split('?')[0].split('#')[0].replace(/\/$/, '');
        if (path === cartPath().replace(/\/$/, '') && path !== '') {
          e.preventDefault();
          openDrawer();
        }
      }
    }, true);

    /* lxd wheel guard: smooth-scroll libraries (Lenis) listen on the document
       and preventDefault the wheel — events that start inside the panel must
       never reach them, so the items list scrolls natively */
    var panelEl = drawer.querySelector('.lxd__panel');
    if (panelEl) {
      ['wheel', 'touchmove'].forEach(function (ev) {
        panelEl.addEventListener(ev, function (e) { e.stopPropagation(); }, { passive: true });
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('lxd--open')) closeDrawer();
    });

    /* detect cart mutations from anywhere in the theme */
    var origFetch = root.fetch;
    root.fetch = function (input, opts) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(function () {
          if (/\/cart\/add/.test(url)) {
            armTimerFromAdd();
            if (!busy && !onCartPage()) openDrawer();
          }
          scheduleRefresh();
        }).catch(function () {});
      }
      return p;
    };

    var foot = $('[data-lxd-foot]');
    if (foot) foot.hidden = (drawer.getAttribute('data-lxd-total') === '0');
    syncTimer();
    timerId = setInterval(renderClock, 1000);

    /* the cart page is a bouncer now: it sets this flag and sends the
       visitor back here with the drawer open */
    try {
      if (sessionStorage.getItem('lxdOpen') === '1') {
        sessionStorage.removeItem('lxdOpen');
        openDrawer();
      }
    } catch (e) {}

    sweepAll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

})(typeof window !== 'undefined' ? window : globalThis);
